import { ApplicationController } from '@aha-app/mvc';
import WhiteboardController from '../controllers/WhiteboardController';
import terminology from '../utils/terminology';
import {
  type CreatedRecordMetadata,
  type CreatableRecordType,
  type CreateParent,
} from '../types';
import trackEvent from '../utils/analytics';
import type {
  CreateRecordData,
  CreateRecordParentId,
  CreateRecord,
  RecordAttributes,
  WorkflowKind,
} from '../shared-types';

interface State {
  recordType: CreatableRecordType;
  data: CreateRecord[];
  selectedProject: CreateParent;
  selectedRelease: CreateParent;
  selectedFeature: CreateParent;
  selectedWorkflowKind?: WorkflowKind;
  submitting: boolean;
}

interface Props {
  recordType: CreatableRecordType;
  data: CreateRecord[];
}

class CreateRecordsController extends ApplicationController<State, Props> {
  static initialState: Partial<State> = {
    /** Initial list of items that will be converted */
    data: [],
    submitting: false,
    selectedProject: { id: null, name: null },
    selectedRelease: { id: null, name: null },
    selectedFeature: { id: null, name: null },
  };

  defaultProject: CreateParent = { id: null, name: null };

  async initialize(props: Props) {
    this.defaultProject = this.whiteboardController.props.defaultProject || {
      id: null,
      name: null,
    };
    this.changeProps(props);
  }

  get whiteboardController() {
    return this.findControllerInstance(WhiteboardController)!;
  }

  get recordManager() {
    return this.whiteboardController.recordManager;
  }

  changeProps(newProps: Props): void {
    if (this.state.recordType !== newProps.recordType) {
      this.state.selectedProject = this.defaultProject;
      this.state.selectedRelease = { id: null, name: null };
      this.state.selectedFeature = { id: null, name: null };
    }

    this.state.recordType = newProps.recordType;
    this.state.data = newProps.data;
  }

  get fieldsForRecordType() {
    switch (this.state.recordType) {
      case 'initiatives':
      case 'ideas':
      case 'goals':
        return {
          project: true,
          release: false,
          parentLines: this.state.recordType !== 'ideas',
        };
      case 'epics':
      case 'features':
        return {
          project: false,
          release: true,
          parentLines: false,
          workflowKind: true,
          description: true,
        };
      case 'requirements':
        return { project: false, release: false, feature: true };
      default:
        return {};
    }
  }

  get areSelectionsValid() {
    const { selectedProject, selectedRelease } = this.state;
    const fields = this.fieldsForRecordType;

    if (fields.project && !selectedProject?.id) {
      return false;
    }

    if (fields.release && !selectedRelease?.id) {
      return false;
    }

    return true;
  }

  actionSetProject(project: CreateParent) {
    this.state.selectedProject = project;
  }

  actionSetRelease(release: CreateParent) {
    this.state.selectedRelease = release;
  }

  actionSetFeature(feature: CreateParent) {
    this.state.selectedFeature = feature;
  }

  actionSetWorkflowKind(kind?: WorkflowKind) {
    this.state.selectedWorkflowKind = kind;
  }

  async actionCreateIdeas(ideas: CreateRecord[]) {
    if (!this.recordManager?.canCreateRecordType('ideas')) return;

    const project = this.state.selectedProject;
    const result = await this.recordManager.createIdeas(
      this.normalizeRecordsForSubmit(ideas, 'projectId', project.id)
    );
    return this.actionPostCreation('idea', 'ideas', project, result);
  }

  async actionCreateFeatures(features: CreateRecord[]) {
    if (!this.recordManager?.canCreateRecordType('features')) return;

    const release = this.state.selectedRelease;
    const workflowKind = this.state.selectedWorkflowKind;
    const attrs = {
      // Copy template from workflow into the new feature
      description: workflowKind?.featureTemplate?.body,
      workflowKind: {
        id: workflowKind?.id,
      },
    };
    const result = await this.recordManager.createFeatures(
      this.normalizeRecordsForSubmit(features, 'releaseId', release.id, attrs)
    );
    return this.actionPostCreation('feature', 'features', release, result);
  }

  async actionCreateRequirements(requirements: CreateRecord[]) {
    if (!this.recordManager?.canCreateRecordType('requirements')) return;

    const feature = this.state.selectedFeature;
    const result = await this.recordManager.createRequirements(
      this.normalizeRecordsForSubmit(requirements, 'featureId', feature.id)
    );
    return this.actionPostCreation(
      'requirement',
      'requirements',
      feature,
      result
    );
  }

  async actionCreateEpics(epics) {
    if (!this.recordManager?.canCreateRecordType('epics')) return;

    const release = this.state.selectedRelease;
    const result = await this.recordManager.createEpics(
      this.normalizeRecordsForSubmit(epics, 'releaseId', release.id)
    );
    return this.actionPostCreation('epic', 'epics', release, result);
  }

  async actionCreateInitiatives(initiatives) {
    if (!this.recordManager?.canCreateRecordType('initiatives')) return;

    const project = this.state.selectedProject;
    const result = await this.recordManager.createInitiatives(
      this.normalizeRecordsForSubmit(initiatives, 'projectId', project.id)
    );
    return this.actionPostCreation(
      'initiative',
      'initiatives',
      project,
      result
    );
  }

  async actionCreateReleases(releases) {
    if (!this.recordManager?.canCreateRecordType('releases')) return;

    const project = this.state.selectedProject;
    const result = await this.recordManager.createReleases(
      this.normalizeRecordsForSubmit(releases, 'projectId', project.id)
    );
    return this.actionPostCreation('release', 'releases', project, result);
  }

  async actionCreateGoals(goals) {
    if (!this.recordManager?.canCreateRecordType('goals')) return;

    const project = this.state.selectedProject;
    const result = await this.recordManager.createGoals(
      this.normalizeRecordsForSubmit(goals, 'projectId', project.id)
    );
    return this.actionPostCreation('goal', 'goals', project, result);
  }

  async actionCreatePdlcSteps(pdlcSteps) {
    if (!this.recordManager?.canCreateRecordType('pdlcSteps')) return;

    const pdlcFrameworkId = window.pdlcFramework?.id;

    const result = await this.recordManager.createPdlcSteps(
      this.normalizeRecordsForSubmit(
        pdlcSteps,
        'pdlcFrameworkId',
        pdlcFrameworkId
      ),
      { pdlcFrameworkId }
    );

    return this.actionPostCreation(
      'pdlcStep',
      'pdlcSteps',
      { id: pdlcFrameworkId, name: window.pdlcFramework?.name || '' },
      result
    );
  }

  actionPostCreation(
    kind: string,
    kinds: string,
    parent: CreateParent,
    result: CreatedRecordMetadata[]
  ) {
    this.whiteboardController.actionAssignCreateRecordsToObjects(
      this.state.recordType,
      result
    );

    const parentName = parent.name || '';

    if (result.length === 1) {
      this.whiteboardController.actionShowMessage({
        message: `New ${terminology(kind)} has been created in ${parentName}`,
        type: 'success',
      });
    } else if (result.length > 1) {
      this.whiteboardController.actionShowMessage({
        message: `${result.length} new ${terminology(
          kinds
        )} created in ${parentName}`,
        type: 'success',
      });
    }
    return result;
  }

  // Generate the records in Aha!.
  async actionCreateRecords(records: CreateRecord[]) {
    if (this.state.submitting) return;
    if (!this.areSelectionsValid) throw new Error('Invalid selections');

    this.state.submitting = true;

    trackEvent('Feature-7094629539429452113', 'User creates records', {
      recordType: this.state.recordType,
      count: records.length,
    });

    switch (this.state.recordType) {
      case 'ideas': {
        await this.actionCreateIdeas(records);
        break;
      }
      case 'features': {
        await this.actionCreateFeatures(records);
        break;
      }
      case 'requirements': {
        await this.actionCreateRequirements(records);
        break;
      }
      case 'epics': {
        await this.actionCreateEpics(records);
        break;
      }
      case 'releases': {
        await this.actionCreateReleases(records);
        break;
      }
      case 'initiatives': {
        await this.actionCreateInitiatives(records);
        break;
      }
      case 'goals': {
        await this.actionCreateGoals(records);
        break;
      }
      case 'pdlcSteps': {
        await this.actionCreatePdlcSteps(records);
        break;
      }
    }

    this.actionAbort();
    this.state.submitting = false;
  }

  actionAbort() {
    this.whiteboardController.actionAbortCreateRecords();
  }

  // Normalize the record to conform to the updated CreateRecordData interface
  private normalizeRecordsForSubmit(
    records: CreateRecord[],
    key: CreateRecordParentId,
    id: RecordId,
    attributes?: RecordAttributes
  ): CreateRecordData[] {
    return records.map(record => ({
      meta: { id: record.id },
      name: record.text,
      [key]: id,
      attributes,
    }));
  }
}

export default CreateRecordsController;
